import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSearch } from '@/hooks/useSearch';
import { SearchBox } from '@/components/search/SearchBox';
import { SearchLanguageFilter } from '@/components/search/SearchLanguageFilter';
import { SearchModeToggle } from '@/components/search/SearchModeToggle';
import { SearchResult } from '@/components/search/SearchResult';
import { SearchResultSkeleton } from '@/components/search/SearchResultSkeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils/cn';
import type { SearchLang, SearchMode } from '@/types/api';

const SAMPLES: { q: string; hint: string }[] = [
  { q: 'patience', hint: 'صبر' },
  { q: 'gratitude', hint: 'شكر' },
  { q: 'forgiveness', hint: 'مغفرة' },
  { q: 'mercy', hint: 'رحمة' },
];

function isLang(v: string | null): v is SearchLang {
  return v === 'en' || v === 'ur' || v === 'ar';
}
function isMode(v: string | null): v is SearchMode {
  return v === 'keyword' || v === 'semantic' || v === 'hybrid';
}

export function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const lang: SearchLang = isLang(params.get('lang')) ? (params.get('lang') as SearchLang) : 'en';
  const mode: SearchMode = isMode(params.get('mode')) ? (params.get('mode') as SearchMode) : 'hybrid';

  useDocumentTitle(q ? `“${q}”` : 'Search');

  const [advanced, setAdvanced] = useState(false);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const search = useSearch({ q, lang, mode });

  return (
    <section className="container-page pt-10 sm:pt-14 pb-16">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
          Search
        </p>
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
          Search the Quran
        </h1>
        <p className="text-sm text-ink-500 dark:text-ink-400 mt-2">
          {mode === 'hybrid'
            ? 'Combining keyword and semantic ranking.'
            : mode === 'keyword'
              ? 'Matching words and stems.'
              : 'Matching meaning.'}
        </p>
      </header>

      <div className="space-y-4 mb-10">
        <SearchBox value={q} onCommit={(v) => updateParam('q', v)} />

        <div className="flex flex-wrap items-center gap-3">
          <SearchLanguageFilter
            value={lang}
            onChange={(v) => updateParam('lang', v)}
          />
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="text-xs font-medium text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-100 transition-colors"
            aria-expanded={advanced}
          >
            {advanced ? 'Hide advanced' : 'Advanced'}
          </button>
          {advanced && (
            <SearchModeToggle
              value={mode}
              onChange={(v) => updateParam('mode', v)}
            />
          )}
        </div>
      </div>

      <Results
        q={q}
        lang={lang}
        isLoading={search.isLoading && q.length > 0}
        isError={search.isError}
        error={search.error}
        data={search.data}
        onSample={(sample) => updateParam('q', sample)}
        onRetry={() => search.refetch()}
      />
    </section>
  );
}

type ResultsProps = {
  q: string;
  lang: SearchLang;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  data: ReturnType<typeof useSearch>['data'];
  onSample: (q: string) => void;
  onRetry: () => void;
};

function Results({
  q,
  lang,
  isLoading,
  isError,
  error,
  data,
  onSample,
  onRetry,
}: ResultsProps) {
  if (!q) {
    return (
      <div>
        <p className="text-sm text-ink-500 dark:text-ink-400 mb-3">
          Try:
        </p>
        <ul className="flex flex-wrap gap-2">
          {SAMPLES.map((s) => (
            <li key={s.q}>
              <button
                type="button"
                onClick={() => onSample(s.q)}
                className={cn(
                  'inline-flex items-center gap-2 px-3 h-8 rounded-full text-sm',
                  'bg-ink-100 dark:bg-ink-900/70 hover:bg-ink-200 dark:hover:bg-ink-800',
                  'text-ink-700 dark:text-ink-200 transition-colors',
                )}
              >
                <span>{s.q}</span>
                <span className="text-ink-400 dark:text-ink-500" dir="rtl">
                  {s.hint}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        tone="error"
        title="Search failed."
        description={error?.message ?? 'Try again in a moment.'}
        onRetry={onRetry}
      />
    );
  }

  if (isLoading) {
    return (
      <ul aria-live="polite" aria-busy="true" className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i}>
            <SearchResultSkeleton />
          </li>
        ))}
      </ul>
    );
  }

  if (!data || data.results.length === 0) {
    return (
      <EmptyState
        title="No matches."
        description="Try different words, or switch the translation language above."
      />
    );
  }

  return (
    <div aria-live="polite">
      <p className="text-xs text-ink-400 dark:text-ink-500 mb-2 num-tight">
        {data.results.length} result{data.results.length === 1 ? '' : 's'} · {data.took_ms} ms
      </p>
      <ul className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
        {data.results.map((r) => (
          <li key={r.ayah_id}>
            <SearchResult result={r} lang={lang} />
          </li>
        ))}
      </ul>
    </div>
  );
}
